import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import type { DataGridProps, GridColDef, GridFilterModel, GridValidRowModel } from '@mui/x-data-grid';
import { arSD, enUS } from '@mui/x-data-grid/locales';
import { useAuth } from '../auth/AuthContext';
import { getUiLanguage, tr } from '../lib/i18n';
import type { Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

const localeText = (getUiLanguage() === 'ar' ? arSD : enUS).components.MuiDataGrid.defaultProps.localeText;

export type { GridColDef, GridFilterModel };

function Overlay({ icon, title, text }: { icon: ReactNode; title: string; text?: string }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: '100%', py: 6, color: 'text.secondary', textAlign: 'center' }}>
      <Box sx={{ width: 56, height: 56, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: (t: Theme) => t.palette.m3.surfaceContainerHigh, '& svg': { fontSize: 28 } }}>{icon}</Box>
      <Typography variant="subtitle2" color="text.primary">{title}</Typography>
      {text && <Typography variant="caption">{text}</Typography>}
    </Stack>
  );
}
const NoRows = () => <Overlay icon={<InboxOutlinedIcon />} title={tr('لا توجد بيانات بعد', 'No data yet')} text={tr('ستظهر السجلات هنا فور إضافتها.', 'Records will appear here once they are added.')} />;
const NoResults = () => <Overlay icon={<SearchOffIcon />} title={tr('لا توجد نتائج مطابقة', 'No matching results')} text={tr('جرّب كلمة بحث أخرى أو أزل بعض عوامل التصفية.', 'Try another search term or remove some filters.')} />;

/**
 * App Data Grid: MUI X Community with the toolbar (columns, filters, export, quick search).
 * `quickFilter` pre-fills the search box (e.g. from ?q= in the URL).
 */
export function DataGrid<R extends GridValidRowModel>(props: DataGridProps<R> & { height?: number | string; quickFilter?: string | null }) {
  const { height = '100%', sx, initialState, quickFilter, slots, slotProps, ...rest } = props;
  const { can } = useAuth();
  const canExport = can('data.export');
  const words = quickFilter?.trim() ? quickFilter.trim().split(/\s+/) : null;
  const state = words
    ? {
        ...initialState,
        filter: {
          ...initialState?.filter,
          filterModel: { items: [], ...initialState?.filter?.filterModel, quickFilterValues: words },
        },
      }
    : initialState;
  return (
    <Box sx={{ height, width: '100%', minHeight: 0 }}>
      <MuiDataGrid
        key={quickFilter ?? ''}
        disableRowSelectionOnClick
        showToolbar
        autoPageSize
        localeText={localeText}
        ignoreDiacritics
        slots={{ noRowsOverlay: NoRows, noResultsOverlay: NoResults, ...slots }}
        slotProps={{
          loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
          ...slotProps,
          // exporting (CSV / print) is the «data.export» permission
          toolbar: { ...slotProps?.toolbar, csvOptions: { disableToolbarButton: !canExport }, printOptions: { disableToolbarButton: !canExport } },
        }}
        initialState={state}
        sx={[
          {
            '& .MuiDataGrid-toolbar': { justifyContent: 'start' },
            '& .MuiDataGrid-toolbar .MuiOutlinedInput-root': {
              borderRadius: 999,
              bgcolor: (t: Theme) => t.palette.m3.surfaceContainerHigh,
              '& .MuiOutlinedInput-notchedOutline': { border: 0 },
              '&.Mui-focused': { bgcolor: '#fff', boxShadow: (t: Theme) => t.shadows[1] },
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...rest}
      />
    </Box>
  );
}
